import { useEffect } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { FaCheckCircle, FaExclamationCircle, FaTimesCircle } from 'react-icons/fa';
import { IoMdClose } from 'react-icons/io';
import { Box } from '../../../components/UI-CHAKRA/Box';
import { Button } from '../../../components/UI-CHAKRA/Button';
import { Flex } from '../../../components/UI-CHAKRA/Flex';
import { Text } from '../../../components/UI-CHAKRA/Text';
import { useModelStore } from '../../../stores/modelStore';
import { useLoading } from '../../../helpers/useLoading';

const AUTO_DISMISS_MS = 6000;

export function ModelFeedbackToast() {
  const feedback = useModelStore((store) => store.feedback);
  const setFeedback = useModelStore((store) => store.setFeedback);
  const undoLastApplication = useModelStore((store) => store.undoLastApplication);
  const lastAppliedItems = useModelStore((store) => store.lastAppliedItems);
  const [undoing, runWithUndo] = useLoading();

  useEffect(() => {
    if (!feedback) return;
    const timer = setTimeout(() => {
      setFeedback(null);
    }, AUTO_DISMISS_MS);
    return () => clearTimeout(timer);
  }, [feedback, setFeedback]);

  const handleUndo = () => {
    runWithUndo(async () => {
      await undoLastApplication();
    });
  };

  const handleClose = () => {
    setFeedback(null);
  };

  return (
    <Box
      position={'fixed'}
      top={{ base: '16px', md: '24px' }}
      right={{ base: '16px', md: '24px' }}
      left={{ base: '16px', md: 'auto' }}
      zIndex={2000}
      pointerEvents={'none'}
    >
      <AnimatePresence>
        {feedback && (
          <motion.div
            key={`${feedback.type}-${feedback.modelName}`}
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.2 }}
            style={{ pointerEvents: 'auto' }}
          >
            {feedback.type === 'success' && (
              <SuccessToast
                modelName={feedback.modelName}
                onUndo={handleUndo}
                onClose={handleClose}
                undoing={undoing}
                canUndo={Boolean(lastAppliedItems)}
                unresolvedCidCount={feedback.unresolvedCidCount}
                unlinkedExamCount={feedback.unlinkedExamCount}
                missingLateralityCount={feedback.missingLateralityCount}
                failedItems={feedback.failedItems}
              />
            )}
            {feedback.type === 'saved' && (
              <SavedToast
                modelName={feedback.modelName}
                unsavedFormulas={feedback.unsavedFormulas}
                onClose={handleClose}
              />
            )}
            {feedback.type === 'removed' && (
              <RemovedToast modelName={feedback.modelName} onClose={handleClose} />
            )}
            {feedback.type === 'error' && (
              <ErrorToast modelName={feedback.modelName} onClose={handleClose} />
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </Box>
  );
}

interface SuccessToastProps {
  modelName: string;
  onUndo: () => void;
  onClose: () => void;
  undoing: boolean;
  canUndo: boolean;
  unresolvedCidCount?: number;
  unlinkedExamCount?: number;
  missingLateralityCount?: number;
  failedItems?: string[];
}

const MAX_LISTED_FAILURES = 3;

/**
 * Formata os rótulos dos itens que falharam, resumindo o excedente.
 *
 * @param items Rótulos dos itens que não puderam ser adicionados.
 * @returns Até três nomes, seguidos de "e mais N" quando houver excedente.
 */
function formatFailedItems(items: string[]): string {
  const listed = items.slice(0, MAX_LISTED_FAILURES).join(', ');
  const rest = items.length - MAX_LISTED_FAILURES;
  return rest > 0 ? `${listed} e mais ${rest}` : listed;
}

function SuccessToast(props: SuccessToastProps) {
  const {
    modelName,
    onUndo,
    onClose,
    undoing,
    canUndo,
    unresolvedCidCount,
    unlinkedExamCount,
    missingLateralityCount,
    failedItems,
  } = props;
  const failedCount = failedItems?.length ?? 0;
  const isPartial = failedCount > 0;

  return (
    <Box
      w={{ base: '100%', md: '356px' }}
      maxW={'356px'}
      p={'16px'}
      bg={isPartial ? '#FFFBF0' : '#F0FBF7'}
      border={`1px solid ${isPartial ? '#FFE0A3' : '#C2FAD2'}`}
      borderRadius={'8px'}
      boxShadow={'0px 4px 3px rgba(0,0,0,0.1)'}
      data-testid={'model-feedback-success'}
      data-partial={isPartial ? 'true' : undefined}
    >
      <Flex direction={'column'} gap={'8px'}>
        <Flex alignItems={'flex-start'} gap={'8px'}>
          <Box flexShrink={0} color={isPartial ? '#C77700' : '#21AA91'}>
            {isPartial ? <FaExclamationCircle size={24} /> : <FaCheckCircle size={24} />}
          </Box>
          <Text
            flex={1}
            fontSize={'16px'}
            fontWeight={600}
            color={'#343A40'}
            lineHeight={'24px'}
            overflow={'hidden'}
            textOverflow={'ellipsis'}
            whiteSpace={'nowrap'}
          >
            {isPartial ? 'Modelo adicionado em parte' : 'Modelo adicionado'}
          </Text>
          <CloseButton onClose={onClose} />
        </Flex>
        {isPartial ? (
          <>
            <Text data-testid={'model-feedback-failed-items'} fontSize={'14px'} color={'#343A40'} lineHeight={'1.3'}>
              {failedCount === 1
                ? `1 item do modelo "${modelName}" não pôde ser adicionado: ${formatFailedItems(failedItems ?? [])}.`
                : `${failedCount} itens do modelo "${modelName}" não puderam ser adicionados: ${formatFailedItems(
                    failedItems ?? []
                  )}.`}
            </Text>
            <Text fontSize={'14px'} color={'#343A40'} lineHeight={'1.3'}>
              {failedCount === 1
                ? 'Os demais itens foram incluídos na prescrição. Adicione esse item manualmente ou desfaça e tente de novo.'
                : 'Os demais itens foram incluídos na prescrição. Adicione esses itens manualmente ou desfaça e tente de novo.'}
            </Text>
          </>
        ) : (
          <Text fontSize={'14px'} color={'#343A40'} lineHeight={'1.2'}>
            {`Os itens do modelo "${modelName}" foram incluídos na prescrição.`}
          </Text>
        )}
        {!!unresolvedCidCount && (
          <Text data-testid={'model-feedback-unresolved-cid'} fontSize={'14px'} color={'#C77700'} lineHeight={'1.2'}>
            {unresolvedCidCount === 1
              ? 'Não foi possível recuperar 1 CID do modelo. Revise o CID antes de emitir.'
              : `Não foi possível recuperar ${unresolvedCidCount} CIDs do modelo. Revise os CIDs antes de emitir.`}
          </Text>
        )}
        {!!unlinkedExamCount && (
          <Text data-testid={'model-feedback-unlinked-exam'} fontSize={'14px'} color={'#C77700'} lineHeight={'1.2'}>
            {unlinkedExamCount === 1
              ? 'Não foi possível vincular 1 exame ao catálogo. Confira os dados do exame antes de emitir.'
              : `Não foi possível vincular ${unlinkedExamCount} exames ao catálogo. Confira os dados dos exames antes de emitir.`}
          </Text>
        )}
        {!!missingLateralityCount && (
          <Text data-testid={'model-feedback-missing-laterality'} fontSize={'14px'} color={'#C77700'} lineHeight={'1.2'}>
            {missingLateralityCount === 1
              ? '1 exame do modelo exige lateralidade. Preencha o lado antes de emitir.'
              : `${missingLateralityCount} exames do modelo exigem lateralidade. Preencha o lado antes de emitir.`}
          </Text>
        )}
        {canUndo && (
          <Button
            variant={'link'}
            onClick={onUndo}
            isDisabled={undoing}
            color={'primary.main'}
            fontSize={'14px'}
            fontWeight={500}
            textDecoration={'underline'}
            justifyContent={'flex-start'}
            p={0}
            h={'auto'}
            minW={'auto'}
            _hover={{ textDecoration: 'underline' }}
            _disabled={{ opacity: 0.6, cursor: 'not-allowed' }}
            data-testid={'model-feedback-undo'}
          >
            {undoing ? 'Desfazendo...' : 'Desfazer'}
          </Button>
        )}
      </Flex>
    </Box>
  );
}

interface SimpleToastProps {
  modelName: string;
  onClose: () => void;
}

function SavedToast(props: SimpleToastProps & { unsavedFormulas?: string[] }) {
  const { modelName, unsavedFormulas, onClose } = props;
  const unsavedCount = unsavedFormulas?.length ?? 0;
  const isPartial = unsavedCount > 0;
  return (
    <Box
      w={{ base: '100%', md: '356px' }}
      maxW={'356px'}
      p={'16px'}
      bg={isPartial ? '#FFFBF0' : '#F0FBF7'}
      border={`1px solid ${isPartial ? '#FFE0A3' : '#C2FAD2'}`}
      borderRadius={'8px'}
      boxShadow={'0px 4px 3px rgba(0,0,0,0.1)'}
      data-testid={'model-feedback-saved'}
      data-partial={isPartial ? 'true' : undefined}
    >
      <Flex direction={'column'} gap={'8px'}>
        <Flex alignItems={'flex-start'} gap={'8px'}>
          <Box flexShrink={0} color={isPartial ? '#C77700' : '#21AA91'}>
            {isPartial ? <FaExclamationCircle size={24} /> : <FaCheckCircle size={24} />}
          </Box>
          <Text
            flex={1}
            fontSize={'16px'}
            fontWeight={600}
            color={'#343A40'}
            lineHeight={'24px'}
            overflow={'hidden'}
            textOverflow={'ellipsis'}
            whiteSpace={'nowrap'}
          >
            {isPartial ? 'Modelo salvo em parte' : 'Modelo salvo'}
          </Text>
          <CloseButton onClose={onClose} />
        </Flex>
        <Text fontSize={'14px'} color={'#343A40'} lineHeight={'1.2'}>
          {`O modelo "${modelName}" foi salvo em Meus modelos.`}
        </Text>
        {isPartial && (
          <Text data-testid={'model-feedback-unsaved-formulas'} fontSize={'14px'} color={'#C77700'} lineHeight={'1.3'}>
            {unsavedCount === 1
              ? `A fórmula ${formatFailedItems(unsavedFormulas ?? [])} não foi incluída: não foi encontrada na sua biblioteca.`
              : `${unsavedCount} fórmulas não foram incluídas (${formatFailedItems(
                  unsavedFormulas ?? []
                )}): não foram encontradas na sua biblioteca.`}
          </Text>
        )}
      </Flex>
    </Box>
  );
}

function RemovedToast(props: SimpleToastProps) {
  const { modelName, onClose } = props;
  return (
    <Box
      w={{ base: '100%', md: '356px' }}
      maxW={'356px'}
      p={'16px'}
      bg={'#F0FBF7'}
      border={'1px solid #C2FAD2'}
      borderRadius={'8px'}
      boxShadow={'0px 4px 3px rgba(0,0,0,0.1)'}
      data-testid={'model-feedback-removed'}
    >
      <Flex direction={'column'} gap={'8px'}>
        <Flex alignItems={'flex-start'} gap={'8px'}>
          <Box flexShrink={0} color={'#21AA91'}>
            <FaCheckCircle size={24} />
          </Box>
          <Text
            flex={1}
            fontSize={'16px'}
            fontWeight={600}
            color={'#343A40'}
            lineHeight={'24px'}
            overflow={'hidden'}
            textOverflow={'ellipsis'}
            whiteSpace={'nowrap'}
          >
            Modelo removido
          </Text>
          <CloseButton onClose={onClose} />
        </Flex>
        <Text fontSize={'14px'} color={'#343A40'} lineHeight={'1.2'}>
          {`O modelo "${modelName}" foi removido com sucesso.`}
        </Text>
      </Flex>
    </Box>
  );
}

interface ErrorToastProps {
  modelName: string;
  onClose: () => void;
}

function ErrorToast(props: ErrorToastProps) {
  const { modelName, onClose } = props;
  return (
    <Box
      w={{ base: '100%', md: '356px' }}
      maxW={'356px'}
      p={'16px'}
      bg={'#FFF4F4'}
      border={'1px solid #FF9292'}
      borderRadius={'8px'}
      boxShadow={'0px 4px 3px rgba(0,0,0,0.1)'}
      data-testid={'model-feedback-error'}
    >
      <Flex direction={'column'} gap={'8px'}>
        <Flex alignItems={'flex-start'} gap={'8px'}>
          <Box flexShrink={0} color={'#FF6161'} pt={'2px'}>
            <FaTimesCircle size={20} />
          </Box>
          <Text flex={1} fontSize={'16px'} fontWeight={600} color={'#343A40'} lineHeight={'1.5'}>
            Erro ao adicionar
          </Text>
          <CloseButton onClose={onClose} />
        </Flex>
        <Text fontSize={'14px'} color={'#343A40'} lineHeight={'1.4'}>
          {`Não foi possível adicionar o modelo "${modelName}". Tente novamente`}
        </Text>
      </Flex>
    </Box>
  );
}

function CloseButton(props: { onClose: () => void }) {
  return (
    <Box
      as={'button'}
      onClick={props.onClose}
      aria-label={'Fechar'}
      color={'#343A40'}
      background={'transparent'}
      border={'none'}
      cursor={'pointer'}
      p={'2px'}
      flexShrink={0}
    >
      <IoMdClose size={24} />
    </Box>
  );
}
